import { useEffect, useState } from 'react'
import { useAppDispatch } from '@/store/store-hooks'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Modal, ModalContent, ModalFooterWithActions, ModalFooterWithActionsType } from '@/components/modal'
import { FilePdf } from '@/assets/icons'
import { ApiError } from '@/util/errors/api-error'
import { isoDateStringToMonthDayYear } from '@/util/time'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackTimelineEvent } from '@/analytics/utils/timeline-analytics'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { getRemovalImpact } from '../../fetch/get-removal-impact'
import { deleteAssetFile } from '../../fetch/delete-asset-file'
import { TimelineAssetActions } from '../../store/slice'
import { RemovalImpactResponse } from '../../store/schemas'

type RemoveFileDialogProps = {
  open: boolean
  assetId: string
  matterId: string
  userFileId: string
  displayName: string
  onClose: () => void
}

/**
 * Remove-file confirmation: fetches the removal impact on open, then synchronously
 * removes the file and regenerates the timeline from the remaining files.
 */
export default function RemoveFileDialog({ open, assetId, matterId, userFileId, displayName, onClose }: RemoveFileDialogProps) {
  const dispatch = useAppDispatch()

  const [impact, setImpact] = useState<RemovalImpactResponse | null>(null)
  const [impactError, setImpactError] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setImpact(null)
    setImpactError(false)
    setRemoveError(null)

    getRemovalImpact(assetId, userFileId)
      .then(setImpact)
      .catch((error) => {
        console.error('Error fetching removal impact:', error)
        setImpactError(true)
      })
  }, [open, assetId, userFileId])

  const handleRemove = async () => {
    if (removing) return
    setRemoving(true)
    setRemoveError(null)
    try {
      const result = await deleteAssetFile(assetId, userFileId)
      trackTimelineEvent(AnalyticsEvent.TimelineAssetFileRemoved, { timelineAssetId: assetId, timelineEventsRemoved: result.events_removed })
      // Server truth: refresh the overview, events, and roster after the regeneration
      dispatch(TimelineAssetActions.fetchAssetForMatter({ matterId }))
      dispatch(TimelineAssetActions.refreshAssetData({ assetId }))
      // Removal is immediate (no regeneration wait), so confirm it with a toast.
      dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Removed ${displayName} from the timeline`, durationMs: kToastDurationShortMs }))
      onClose()
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setRemoveError('The timeline is being updated. Try again when the current update finishes.')
      } else {
        setRemoveError('Could not remove the file. Please try again.')
      }
    } finally {
      setRemoving(false)
    }
  }

  if (!open) return null

  const impactSentence = (() => {
    if (impactError) return 'The impact summary could not be loaded. Removing this file will delete its events and regenerate the timeline.'
    if (!impact) return null
    const eventsPhrase = `${impact.events_count} event${impact.events_count === 1 ? '' : 's'}`
    if (impact.earliest_event_date && impact.latest_event_date) {
      return `Removing this file will delete ${eventsPhrase} from ${isoDateStringToMonthDayYear(impact.earliest_event_date)} – ${isoDateStringToMonthDayYear(
        impact.latest_event_date
      )} and regenerate the timeline.`
    }
    return `Removing this file will delete ${eventsPhrase} and regenerate the timeline.`
  })()

  const footer = (
    <ModalFooterWithActions
      type={ModalFooterWithActionsType.DESTRUCTIVE}
      secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: removing }}
      primaryAction={{ label: 'Remove and regenerate', onClick: handleRemove, disabled: removing || impactSentence === null, loading: removing }}
    />
  )

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !removing) onClose()
      }}
    >
      <ModalContent title="Remove file from timeline" width="md" height="max-md" footer={footer}>
        <div className="flex flex-col gap-2">
          {impactSentence === null ? (
            <div className="flex items-center gap-2 text-sm text-brand-neutral-600">
              <CircularProgressContinuousSized size={14} thickness={4} />
              Calculating impact…
            </div>
          ) : (
            <p className="text-base leading-6 tracking-[-0.32px] text-brand-neutral-950">{impactSentence}</p>
          )}

          <div className="flex items-center gap-2.5">
            <FilePdf className="w-6 h-6 text-brand-neutral-950 shrink-0" />
            <span className="text-sm font-medium tracking-[-0.15px] text-brand-neutral-950 truncate" title={displayName}>
              {displayName}
            </span>
          </div>

          {removeError && <p className="text-sm text-red-700">{removeError}</p>}
        </div>
      </ModalContent>
    </Modal>
  )
}
